import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch,
  Table, Tabs, Tag, Typography, message,
} from 'antd';
import TableauMensuel from './TableauMensuel';
import EcolesAssociees from './EcolesAssociees';
import Corrections from './Corrections';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, MinusCircleOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../components/layout/AppLayout';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import { CODIFICATION_ROLES } from '../../../constant/codification';
import {
  lienParrainage, useApporteursApi, type TApporteur, type TApporteurCree, type TAvantage,
  type TTypeAvantage,
} from '../../../api/paiement/apporteurs';
import { libelleAvantage } from '../../apporteur/avantage';

// Administration des animateurs apporteurs d'affaires : création du compte
// (mot de passe provisoire affiché UNE fois), code et avantage tarifaire,
// activation.
//
// Les règles (code disponible, avantage cohérent) sont vérifiées par le
// serveur : l'écran affiche son refus tel quel.

const DEVISES = ['MAD', 'EUR', 'USD'].map((d) => ({ label: d, value: d }));

type AvantageForm = {
  type: TTypeAvantage;
  valeur?: number;
  devise?: string;
  prix?: { devise: string; prix: number }[];
};

type CreationForm = {
  prenom: string;
  nom: string;
  email: string;
  code: string;
  avantage: AvantageForm;
};

type ParrainageForm = {
  code: string;
  avantage: AvantageForm;
};

const versAvantage = (f?: AvantageForm): TAvantage => {
  if (!f || f.type === 'aucun') return { type: 'aucun' };
  if (f.type === 'pourcentage') return { type: 'pourcentage', valeur: f.valeur };
  if (f.type === 'montant_fixe') return { type: 'montant_fixe', valeur: f.valeur, devise: f.devise };
  return {
    type: 'prix_fixe',
    prix: (f.prix ?? []).map((p) => ({ offre: 'PREMIUM_INDIVIDUEL', devise: p.devise, prix: p.prix })),
  };
};

const depuisAvantage = (a?: TAvantage): AvantageForm => ({
  type: a?.type ?? 'aucun',
  valeur: a?.valeur,
  devise: a?.devise,
  prix: (a?.prix ?? []).map((p) => ({ devise: p.devise, prix: p.prix })),
});

const erreurServeur = (err: unknown): string | undefined =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error;

/** Champs de l'avantage tarifaire, communs à la création et à la modification. */
const ChampsAvantage: React.FC = () => {
  const { t } = useTranslation();
  const type = Form.useWatch(['avantage', 'type']) as TTypeAvantage | undefined;
  return (
    <>
      <Form.Item name={['avantage', 'type']} label={t('apporteurs.admin.avantage', 'Avantage tarifaire')} initialValue="aucun">
        <Select
          options={[
            { value: 'aucun', label: t('apporteurs.admin.avantageAucun', 'Aucun (tarif public)') },
            { value: 'pourcentage', label: t('apporteurs.admin.avantagePourcentage', 'Pourcentage de réduction') },
            { value: 'montant_fixe', label: t('apporteurs.admin.avantageMontant', 'Montant retiré par mois') },
            { value: 'prix_fixe', label: t('apporteurs.admin.avantagePrixFixe', 'Prix mensuel fixe') },
          ]}
        />
      </Form.Item>
      {type === 'pourcentage' && (
        <Form.Item
          name={['avantage', 'valeur']}
          label={t('apporteurs.admin.pourcentage', 'Pourcentage')}
          rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}
        >
          <InputNumber min={0.01} max={99.99} step={1} addonAfter="%" style={{ width: '100%' }} />
        </Form.Item>
      )}
      {type === 'montant_fixe' && (
        <Space.Compact style={{ width: '100%' }}>
          <Form.Item
            name={['avantage', 'valeur']}
            label={t('apporteurs.admin.montant', 'Montant retiré par mois')}
            style={{ flex: 1 }}
            rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}
          >
            <InputNumber min={0.01} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name={['avantage', 'devise']}
            label={t('apporteurs.admin.devise', 'Devise')}
            initialValue="MAD"
            rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}
          >
            <Select options={DEVISES} style={{ width: 110 }} />
          </Form.Item>
        </Space.Compact>
      )}
      {type === 'prix_fixe' && (
        <Form.List name={['avantage', 'prix']} initialValue={[{ devise: 'MAD', prix: undefined }]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Space key={field.key} align="baseline">
                  <Form.Item name={[field.name, 'devise']} rules={[{ required: true }]}>
                    <Select options={DEVISES} style={{ width: 110 }} />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'prix']}
                    rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}
                  >
                    <InputNumber min={0.01} step={1} placeholder={t('apporteurs.admin.prixMensuel', 'Prix par mois')} />
                  </Form.Item>
                  {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(field.name)} />}
                </Space>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ devise: 'EUR' })}>
                {t('apporteurs.admin.ajouterDevise', 'Ajouter une devise')}
              </Button>
              <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t('apporteurs.admin.prixFixeAide',
                  "Le prix fixe ne s'applique qu'aux familles payant dans une devise listée ; les autres paient le tarif public.")}
              </Typography.Paragraph>
            </>
          )}
        </Form.List>
      )}
    </>
  );
};

const ApporteursAdmin: React.FC = () => {
  const { t } = useTranslation();
  const api = useApporteursApi();
  const { isRoleExist } = useTilmiAuth();
  const peutGererComptes = isRoleExist(CODIFICATION_ROLES.ADMIN_USERS);

  const [apporteurs, setApporteurs] = useState<TApporteur[]>([]);
  const [loading, setLoading] = useState(false);
  const [creationOuverte, setCreationOuverte] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [cree, setCree] = useState<TApporteurCree | null>(null);
  const [edite, setEdite] = useState<TApporteur | null>(null);
  const [formCreation] = Form.useForm<CreationForm>();
  const [formParrainage] = Form.useForm<ParrainageForm>();

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      setApporteurs(await api.lister());
    } catch {
      message.error(t('apporteurs.admin.chargementErreur', 'Échec du chargement des animateurs'));
    } finally {
      setLoading(false);
    }
    // api change d'identité à chaque rendu : le laisser en dépendance
    // relancerait le chargement en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  const creer = async (v: CreationForm) => {
    setEnCours(true);
    try {
      const resultat = await api.creer({
        prenom: v.prenom, nom: v.nom, email: v.email, code: v.code, avantage: versAvantage(v.avantage),
      });
      setCreationOuverte(false);
      formCreation.resetFields();
      setCree(resultat);
      charger();
    } catch (err) {
      message.error(erreurServeur(err) || t('apporteurs.admin.creationErreur', "Échec de la création de l'animateur"));
    } finally {
      setEnCours(false);
    }
  };

  const ouvrirParrainage = (a: TApporteur) => {
    setEdite(a);
    formParrainage.setFieldsValue({ code: a.code?.code ?? '', avantage: depuisAvantage(a.code?.avantage) });
  };

  const enregistrerParrainage = async (v: ParrainageForm) => {
    if (!edite) return;
    setEnCours(true);
    try {
      await api.definirCode(edite.id, v.code, versAvantage(v.avantage));
      message.success(t('apporteurs.admin.parrainageEnregistre', 'Code et avantage enregistrés'));
      setEdite(null);
      charger();
    } catch (err) {
      message.error(erreurServeur(err) || t('apporteurs.admin.enregistrementErreur', "Échec de l'enregistrement"));
    } finally {
      setEnCours(false);
    }
  };

  const basculer = async (a: TApporteur) => {
    try {
      await api.definirActif(a.id, !a.actif);
      charger();
    } catch (err) {
      message.error(erreurServeur(err) || t('apporteurs.admin.enregistrementErreur', "Échec de l'enregistrement"));
    }
  };

  const colonnes: ColumnsType<TApporteur> = [
    {
      title: t('apporteurs.admin.animateur', 'Animateur'),
      key: 'animateur',
      render: (_, a) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{[a.prenom, a.nom].filter(Boolean).join(' ') || a.login}</Typography.Text>
          <Typography.Text type="secondary">{a.email}</Typography.Text>
        </Space>
      ),
    },
    {
      title: t('apporteurs.admin.code', 'Code'),
      key: 'code',
      render: (_, a) => a.code ? (
        <Space>
          <Typography.Text code copyable={{ text: a.code.code }}>{a.code.code}</Typography.Text>
          {a.code.actif
            ? <Tag color="green">{t('apporteur.codeActif', 'Actif')}</Tag>
            : <Tag>{t('apporteur.codeInactif', 'Inactif')}</Tag>}
        </Space>
      ) : '—',
    },
    {
      title: t('apporteurs.admin.avantage', 'Avantage tarifaire'),
      key: 'avantage',
      render: (_, a) => libelleAvantage(t, a.code?.avantage),
    },
    {
      title: t('apporteurs.admin.lien', 'Lien de parrainage'),
      key: 'lien',
      render: (_, a) => a.code?.actif ? (
        <Typography.Text copyable={{ text: lienParrainage(a.code.code, a.lien_parrainage) }}>
          {t('apporteurs.admin.copierLien', 'Copier')}
        </Typography.Text>
      ) : '—',
    },
    {
      title: t('apporteurs.admin.actif', 'Actif'),
      key: 'actif',
      render: (_, a) => (
        <Popconfirm
          title={a.actif
            ? t('apporteurs.admin.confirmerDesactivation', "Désactiver cet animateur ? Son code n'enregistrera plus de nouveaux enfants ; les enfants déjà apportés restent comptés.")
            : t('apporteurs.admin.confirmerActivation', 'Réactiver cet animateur et son dernier code ?')}
          onConfirm={() => basculer(a)}
          disabled={!peutGererComptes}
        >
          <Switch checked={a.actif} disabled={!peutGererComptes} />
        </Popconfirm>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, a) => (
        <Button icon={<EditOutlined />} onClick={() => ouvrirParrainage(a)}>
          {t('apporteurs.admin.modifierParrainage', 'Code et avantage')}
        </Button>
      ),
    },
  ];

  return (
    <AppLayout title={t('apporteurs.admin.titre', 'Animateurs apporteurs')}>
      <Tabs
        items={[
          {
            key: 'animateurs',
            label: t('apporteurs.admin.ongletAnimateurs', 'Animateurs'),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {peutGererComptes && (
                  <Button type="primary" icon={<UserAddOutlined />} onClick={() => setCreationOuverte(true)}>
                    {t('apporteurs.admin.nouveau', 'Nouvel animateur')}
                  </Button>
                )}
                <Table rowKey="id" columns={colonnes} dataSource={apporteurs} loading={loading} scroll={{ x: true }} />
              </Space>
            ),
          },
          {
            key: 'tableau',
            label: t('apporteurs.admin.ongletTableau', 'Tableau mensuel'),
            children: <TableauMensuel />,
          },
          {
            key: 'ecoles',
            label: t('apporteurs.admin.ongletEcoles', 'Écoles associées'),
            children: <EcolesAssociees apporteurs={apporteurs} peutGerer={peutGererComptes} />,
          },
          {
            key: 'corrections',
            label: t('apporteurs.admin.ongletCorrections', 'Corrections'),
            children: <Corrections apporteurs={apporteurs} peutGerer={peutGererComptes} />,
          },
        ]}
      />

      <Modal
        title={t('apporteurs.admin.nouveau', 'Nouvel animateur')}
        open={creationOuverte}
        onCancel={() => setCreationOuverte(false)}
        onOk={() => formCreation.submit()}
        confirmLoading={enCours}
        destroyOnClose
      >
        <Form form={formCreation} layout="vertical" onFinish={creer} preserve={false}>
          <Form.Item name="prenom" label={t('apporteurs.admin.prenom', 'Prénom')} rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}>
            <Input />
          </Form.Item>
          <Form.Item name="nom" label={t('apporteurs.admin.nom', 'Nom')} rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label={t('apporteurs.admin.email', 'Email (identifiant de connexion)')} rules={[{ required: true, type: 'email', message: t('apporteurs.admin.emailInvalide', 'Email invalide') }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="code"
            label={t('apporteurs.admin.code', 'Code')}
            extra={t('apporteurs.admin.codeAide', '6 à 32 lettres ou chiffres, par exemple KARIM2026. Majuscules et espaces sont indifférents.')}
            rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}
          >
            <Input />
          </Form.Item>
          <ChampsAvantage />
        </Form>
      </Modal>

      <Modal
        title={t('apporteurs.admin.compteCree', 'Animateur créé')}
        open={!!cree}
        onOk={() => setCree(null)}
        onCancel={() => setCree(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
        closable={false}
        maskClosable={false}
      >
        {cree && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message={t('apporteurs.admin.motDePasseUneFois', 'Mot de passe provisoire affiché une seule fois')}
              description={t('apporteurs.admin.motDePasseAide',
                "Transmettez-le à l'animateur : il devra le changer à sa première connexion. Il ne pourra plus être réaffiché.")}
            />
            <Typography.Text>{t('apporteurs.admin.identifiant', 'Identifiant')} : <Typography.Text code copyable>{cree.apporteur.email}</Typography.Text></Typography.Text>
            <Typography.Text>{t('apporteurs.admin.motDePasse', 'Mot de passe provisoire')} : <Typography.Text code copyable>{cree.mot_de_passe_provisoire}</Typography.Text></Typography.Text>
          </Space>
        )}
      </Modal>

      <Modal
        title={t('apporteurs.admin.modifierParrainage', 'Code et avantage')}
        open={!!edite}
        onCancel={() => setEdite(null)}
        onOk={() => formParrainage.submit()}
        confirmLoading={enCours}
        destroyOnClose
      >
        <Form form={formParrainage} layout="vertical" onFinish={enregistrerParrainage} preserve={false}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('apporteurs.admin.remplacementCode',
              "Changer de code désactive l'ancien : ses liens afficheront « code inactif ». Les enfants déjà apportés restent attribués à l'animateur.")}
          />
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('apporteurs.admin.carteAbonnement',
              "Paiement par carte : le prix est fixé à la création de l'abonnement. Une modification de l'avantage ne s'applique qu'aux nouveaux abonnements.")}
          />
          <Form.Item name="code" label={t('apporteurs.admin.code', 'Code')} rules={[{ required: true, message: t('apporteurs.admin.requis', 'Champ requis') }]}>
            <Input />
          </Form.Item>
          <ChampsAvantage />
        </Form>
      </Modal>
    </AppLayout>
  );
};

export default ApporteursAdmin;
